// src/contexts/SupplierProgramsContext.jsx
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useUser } from './UserContext';

const ENV_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_BASE) ||
  process.env.REACT_APP_API_BASE ||
  '';
const CLEAN_ENV_BASE = ENV_BASE.replace(/\/+$/, '');
const FALLBACK_BASE = 'https://www.doroosonline.co.il';
const API_BASE = CLEAN_ENV_BASE || FALLBACK_BASE;
const url = (path) => `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;

const DEFAULT_TIMEOUT_MS = 12000;

const getStoredToken = () => {
  try {
    return localStorage.getItem('jwt') ?? localStorage.getItem('token') ?? null;
  } catch {
    return null;
  }
};

async function parseJSONSafely(res) {
  if (res.status === 204) return {};
  const text = await res.text().catch(() => '');
  if (!text) return {};
  try { return JSON.parse(text); } catch { return { raw: text }; }
}

async function apiFetch(path, { method = 'GET', body, timeout = DEFAULT_TIMEOUT_MS } = {}) {
  const token = getStoredToken();
  const hasBody = body !== undefined;

  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), timeout);

  try {
    const res = await fetch(url(path), {
      method,
      headers: {
        ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: hasBody ? JSON.stringify(body) : undefined,
      mode: 'cors',
      cache: 'no-store',
      credentials: 'omit',
      signal: controller.signal,
    });

    const data = await parseJSONSafely(res);

    if (!res.ok) {
      const msg =
        data?.error ||
        (typeof data?.error === 'object' ? Object.values(data.error)[0] : null) ||
        (res.status === 401 ? 'שגיאה בהרשאות (401)' : `שגיאת שרת (${res.status})`);
      throw new Error(msg);
    }

    return data ?? {};
  } catch (e) {
    if (e.name === 'AbortError') throw new Error('זמן ההמתנה לשרת הסתיים. נסה שוב.');
    if (e instanceof TypeError) {
      throw new Error(`שגיאת רשת: לא ניתן להגיע לשרת ב- ${API_BASE}.`);
    }
    throw e;
  } finally {
    clearTimeout(t);
  }
}

const SupplierProgramsContext = createContext(null);

export const SupplierProgramsProvider = ({ children }) => {
  const { role, userType, showAlert, ready, isLogedin } = useUser();
  const isSupplier = role === 'supplier' || userType === 'supplier';

  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const fetchPrograms = useCallback(async () => {
    if (!isSupplier) return [];
    const startedAt = Date.now();
    setLoading(true);
    try {
      const data = await apiFetch('/api/supplier/programs');
      const items = Array.isArray(data?.items) ? data.items : [];
      setPrograms(items);
      setLoaded(true);
      return items;
    } catch (err) {
      showAlert?.(err.message || 'שגיאה בטעינת תוכניות גפ״ן', 'warning');
      return [];
    } finally {
      // Keep the spinner visible for at least this long — a request
      // that resolves in a frame or two (fast server, cached
      // connection) would otherwise flip loading true->false too
      // quickly for a human to ever actually see it, even though the
      // state and the SpinnerOverlay render are both working correctly.
      const MIN_VISIBLE_MS = 400;
      const elapsed = Date.now() - startedAt;
      const remaining = MIN_VISIBLE_MS - elapsed;
      if (remaining > 0) {
        await new Promise((resolve) => setTimeout(resolve, remaining));
      }
      setLoading(false);
    }
  }, [isSupplier, showAlert]);

  const addProgram = useCallback(async (payload) => {
    const data = await apiFetch('/api/supplier/programs', { method: 'POST', body: payload });
    const created = data?.program;
    if (created) setPrograms((prev) => [created, ...prev]);
    return created;
  }, []);

  const updateProgram = useCallback(async (id, payload) => {
    const data = await apiFetch(`/api/supplier/programs/${id}`, { method: 'PATCH', body: payload });
    const updated = data?.program;
    if (updated) {
      setPrograms((prev) => prev.map((p) => (p.id === id ? updated : p)));
    }
    return updated;
  }, []);

  const changeProgramStatus = useCallback(
    (id, status) => updateProgram(id, { status }),
    [updateProgram]
  );

  const archiveProgram = useCallback(
    (id) => updateProgram(id, { status: 'CLOSED' }),
    [updateProgram]
  );

  useEffect(() => {
    // Wait for UserContext to finish verifying the stored token with
    // the server (ready) and confirm there's an actual active session
    // (isLogedin) before fetching anything. Without this, `role` can
    // briefly hold a stale value from localStorage on page load —
    // from a *previous* session, before the token's even been checked
    // — so this would otherwise fire on the login page itself using a
    // possibly-expired token, which is exactly what was happening.
    if (!ready) return;

    if (!isLogedin) {
      // Logged out (or a fresh, never-logged-in load): drop whatever
      // was loaded before so it can't leak into a different account
      // logging in afterward on the same browser.
      setPrograms([]);
      setLoaded(false);
      return;
    }

    if (!isSupplier) return;
    fetchPrograms();
  }, [ready, isLogedin, isSupplier, fetchPrograms]);

  const value = useMemo(
    () => ({
      programs,
      loading,
      loaded,
      fetchPrograms,
      addProgram,
      updateProgram,
      changeProgramStatus,
      archiveProgram,
    }),
    [programs, loading, loaded, fetchPrograms, addProgram, updateProgram, changeProgramStatus, archiveProgram]
  );

  return (
    <SupplierProgramsContext.Provider value={value}>
      {children}
    </SupplierProgramsContext.Provider>
  );
};

export const useSupplierPrograms = () => {
  const ctx = useContext(SupplierProgramsContext);
  if (!ctx) throw new Error('useSupplierPrograms must be used within a SupplierProgramsProvider');
  return ctx;
};